import React, { memo } from "react";
import { CleanedData } from "../../types/global.d";
import ReportDetailRow from "./ReportDetailRow";
import CleanupSeparator from "./CleanupSeparator";
import PollutionAmountBadge from "./PollutionAmountBadge";
import { getCleanupTypeLabel } from "../../utils/reportFormatting";
import i18n from "../../i18n/config";
import { useTranslation } from "react-i18next";

interface ReportCleanupDetailsProps {
  cleanedData: CleanedData;
  showSectionHeading?: boolean;
  onUserClick?: (
    nickname: string,
    avatar?: string,
    fullname?: string,
    vkUserId?: string
  ) => void;
  canEdit?: boolean;
  onEdit?: () => void;
}

const ReportCleanupDetails: React.FC<ReportCleanupDetailsProps> = memo(
  ({
    cleanedData,
    showSectionHeading = true,
    onUserClick,
    canEdit,
    onEdit,
  }) => {
    const cleanupDate = cleanedData.date || "No cleanup date";
    const cleanerNickname = cleanedData.user?.nickname
      ? `@${cleanedData.user.nickname}`
      : "Unknown";
    const cleanupStatus = cleanedData.verified
      ? (i18n.t as any)("report.verifiedCleanup")
      : (i18n.t as any)("report.pendingVerification");
    const cleanupTypeLabel = getCleanupTypeLabel(cleanedData);
    const { t } = useTranslation();
    return (
      <>
        {showSectionHeading && (
          <CleanupSeparator canEdit={canEdit} onEdit={onEdit} />
        )}

        <ReportDetailRow label={t("cleanup.cleanupDate")} value={cleanupDate} />

        <ReportDetailRow
          label={t("cleanup.cleanedBy")}
          value={cleanerNickname}
          clickable={!!onUserClick && !!cleanedData.user?.nickname}
          onClick={
            onUserClick && cleanedData.user?.nickname
              ? () =>
                  onUserClick(
                    cleanedData.user.nickname,
                    cleanedData.user.avatar,
                    cleanedData.user.fullname,
                    cleanedData.user.vkUserId
                  )
              : undefined
          }
        />

        {cleanedData.comment && (
          <ReportDetailRow
            label={t("cleanup.cleanupComment")}
            value={cleanedData.comment}
          />
        )}

        {cleanupTypeLabel && (
          <ReportDetailRow
            label={t("cleanup.cleanupType")}
            value={cleanupTypeLabel}
          />
        )}

        {typeof cleanedData.amount === "number" && (
          <div className="detail-row">
            <span className="detail-label">{t("cleanup.cleanupAmount")}:</span>
            <PollutionAmountBadge amount={cleanedData.amount} />
          </div>
        )}

        <ReportDetailRow
          label={t("cleanup.cleanupStatus")}
          value={cleanupStatus}
        />
      </>
    );
  }
);

ReportCleanupDetails.displayName = "ReportCleanupDetails";

export default ReportCleanupDetails;
